/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2018 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
import React from 'react';

import { MapTo } from '@adobe/aem-react-editable-components';
import PropTypes from 'prop-types';

import extractModelId from 'utils/extract-model-id';

import './css/Image.scss';

/**
 * Default Edit configuration for the Image component that interact with the Core Image component and sub-types
 *
 * @type EditConfig
 */
const ImageEditConfig = {
  emptyLabel: 'Image',

  isEmpty(props) {
    return !props || !props.src;
  },
};

/**
 * Expected usage of the Image Component.
 *
 * <Image
 *      cqModel='the whole Object holding information for the image from the model.json response' />
 *
 * Please see the package.json for the proxy settings.
 */
export const Image = ({ src, alt, title, id, cqPath, link }) => {
  const imageProps = {
    src,
    alt,
    title,
    id: id || extractModelId(cqPath),
    hasLink: Boolean(link),
    link,
  };

  const content = (
    <img
      className="efix-image"
      id={imageProps.id}
      src={imageProps.src}
      alt={imageProps.alt || ''}
      title={imageProps.title || ''}
    />
  );

  const linkedContent = (
    <a href={imageProps.link} title={imageProps.title || imageProps.alt}>
      {content}
    </a>
  );

  const innerContent = imageProps.hasLink ? linkedContent : content;
  return <div className="cmp-image">{innerContent}</div>;
};

Image.defaultProps = {
  src: '',
  alt: '',
  title: '',
  id: '',
  cqPath: '',
  link: '',
};

Image.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  cqPath: PropTypes.string,
  link: PropTypes.string,
};

MapTo('e-fix/components/content/image')(Image, ImageEditConfig);
